import { useState, SetStateAction, CSSProperties } from "react";

import { css } from "@compiled/react";
import Select from "react-select";

import { token } from "@atlaskit/tokens";

const dropdownSelectorContainerStyle = css({
    display: "inline-flex",
    flexDirection: "column",
    width: "100%"
});

const labelStyle = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#42526e",
    fontSize: "0.75rem",
    lineHeight: "1.1",
    marginBlockEnd: token("space.050"),
    textAlign: "left"
});
const labelStyleSignup = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#42526e",
    lineHeight: "1.1",
    marginBlockEnd: token("space.050"),
    textAlign: "left",
    fontWeight: 500,
    fontSize: "14px"
});

interface Option {
    readonly value: string;
    readonly label: string;
}

export interface DropdownSelectorProps {
    readonly options: Option[];
    readonly name: string;
    readonly label: string;
    readonly id?: string;
    readonly testId?: string;
    readonly onChangeHandler?: React.Dispatch<SetStateAction<string>>;
    readonly className?: string;
    readonly cssOverrides?: {
        readonly container?: CSSProperties;
        readonly control?: CSSProperties;
        readonly dropdownIndicator?: CSSProperties;
        readonly indicatorsContainer?: CSSProperties;
        readonly input?: CSSProperties;
        readonly menu?: CSSProperties;
        readonly option?: CSSProperties;
        readonly placeholder?: CSSProperties;
        readonly singleValue?: CSSProperties;
        readonly valueContainer?: CSSProperties;
        readonly labelStyle?: "signup";
    };
    readonly defaultValue?: Option;
}

export const DropdownSelector = ({
    options,
    name,
    id,
    testId,
    label,
    onChangeHandler,
    defaultValue,
    cssOverrides,
    className
}: DropdownSelectorProps) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const elementID = id ? id : `${name}-dropdown`;

    return (
        <div className={className} css={dropdownSelectorContainerStyle}>
            {cssOverrides?.labelStyle === "signup" && (
                <label css={labelStyleSignup} htmlFor={elementID}>
                    {label}
                </label>
            )}
            {!cssOverrides?.labelStyle && (
                <label css={labelStyle} htmlFor={elementID}>
                    {label}
                </label>
            )}
            <div
                className="select-dropdown-container"
                data-testid={testId ? testId : elementID}
            >
                <Select<Option>
                    defaultValue={
                        defaultValue || { label: "Select...", value: "" }
                    }
                    options={options}
                    unstyled={true}
                    inputId={elementID}
                    onChange={(selectedOption) => {
                        onChangeHandler?.(selectedOption?.value || "");
                    }}
                    onMenuOpen={() => setIsMenuOpen(true)}
                    onMenuClose={() => setIsMenuOpen(false)}
                    styles={{
                        // @ts-ignore
                        container: (provided) => ({
                            ...provided,
                            appearance: "none",
                            flexDirection: "row",
                            fontSize: "1rem",
                            lineHeight: "1.2",
                            textAlign: "left",
                            ...(cssOverrides || {}).container
                        }),
                        // @ts-ignore
                        singleValue: (provided) => ({
                            ...provided,
                            marginLeft: token("space.025"),
                            marginRight: token("space.025"),
                            ...(cssOverrides || {}).singleValue
                        }),
                        // @ts-ignore
                        placeholder: (provided) => ({
                            ...provided,
                            marginLeft: token("space.025"),
                            marginRight: token("space.025"),
                            ...(cssOverrides || {}).placeholder
                        }),
                        // @ts-ignore
                        valueContainer: (provided) => ({
                            ...provided,
                            paddingLeft: token("space.075"),
                            paddingRight: token("space.075"),
                            ...(cssOverrides || {}).valueContainer
                        }),
                        // @ts-ignore
                        menu: (provided) => ({
                            ...provided,
                            marginTop: token("space.050"),
                            // eslint-disable-next-line local/ensure-wac-token-usage
                            boxShadow: "0px 3px 5px 0px #091E4233",
                            // eslint-disable-next-line local/ensure-wac-token-usage
                            background: "#ffffff",
                            padding: 0,
                            borderRadius: token("space.050"),
                            ...(cssOverrides || {}).menu
                        }),
                        // @ts-ignore
                        control: (provided, state) => ({
                            ...provided,
                            "cursor": "pointer",
                            "backgroundColor": isMenuOpen
                                ? "#253858"
                                : "#FAFBFC",
                            "color": isMenuOpen ? "#ffffff" : "#172B4D",
                            "height": "2.65rem",
                            "border": state.isFocused
                                ? `2px solid #4C9AFF`
                                : `2px solid #dfe1e6`,
                            "borderRadius": token("space.050"),
                            "&:hover": {
                                background: isMenuOpen ? undefined : "#F4F5F7"
                            },
                            ...(cssOverrides || {}).control
                        }),
                        // @ts-ignore
                        dropdownIndicator: (provided, state) => ({
                            ...provided,
                            transition: "all .2s ease",
                            transform: state.selectProps.menuIsOpen
                                ? "rotate(180deg)"
                                : undefined,
                            ...(cssOverrides || {}).dropdownIndicator
                        }),
                        // @ts-ignore
                        indicatorsContainer: (provided) => ({
                            ...provided,
                            paddingTop: token("space.075"),
                            paddingBottom: token("space.075"),
                            paddingLeft: token("space.025"),
                            paddingRight: token("space.075"),
                            ...(cssOverrides || {}).indicatorsContainer
                        }),
                        // @ts-ignore
                        option: (provided, state) => ({
                            ...provided,
                            "cursor": "pointer",
                            "padding": `${token("space.100")} ${token(
                                "space.200"
                            )}`,
                            "whiteSpace": "pre-wrap",
                            "overflowWrap": "break-word",
                            "backgroundColor": state.isSelected
                                ? "#B2D4FF"
                                : state.isFocused
                                ? "#EBECF0"
                                : "#FAFBFC",
                            "color": state.isSelected ? "#0065FF" : "#172B4D",
                            "&:hover": {
                                background: state.isSelected
                                    ? "#B2D4FF"
                                    : "#EBECF0"
                            },
                            "&:active": {
                                // eslint-disable-next-line local/ensure-wac-token-usage
                                background: "#DFE1E5"
                            },
                            ...(cssOverrides || {}).option
                        }),
                        // @ts-ignore
                        input: (provided) => ({
                            ...provided,
                            color: "transparent",
                            ...(cssOverrides || {}).input
                        })
                    }}
                />
            </div>
        </div>
    );
};
